import "./EmailVerified.css";
import { Button, Form } from "react-bootstrap";
import { Logo } from "../logo/logo";
import { Container, ErrorText, InnerContainer } from "../commons";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { createT } from "../../i18n/strings";
import axios from "axios";

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

const EmailVerified = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Auth pages are intentionally Swedish-only (see createT) so signup never
  // flips between languages mid-flow regardless of the stored UI language.
  const t = useMemo(() => createT("sv"), []);
  const success = searchParams.get("success") === "true";
  const error = searchParams.get("error");

  const [email, setEmail] = useState("");
  const [resendError, setResendError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Force light theme
  useEffect(() => {
    const prev = document.documentElement.getAttribute("data-bs-theme");
    document.documentElement.setAttribute("data-bs-theme", "light");
    return () => {
      if (prev) document.documentElement.setAttribute("data-bs-theme", prev);
    };
  }, []);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setResendError(t("emailVerification.emailRequired"));
      return;
    }

    setIsSubmitting(true);
    setResendError("");

    try {
      await axios.post(`${apiUrl}/api/verify/resend`, { email });
      setResendSuccess(true);
    } catch (err: any) {
      if (err.response?.data?.error === "AlreadyVerified") {
        setResendError(t("emailVerification.alreadyVerified"));
      } else {
        setResendError(t("emailVerification.resendFailed"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <Container className="login-container email-verified-container">
        <span className="auth-blob b1" aria-hidden="true" />
        <span className="auth-blob b2" aria-hidden="true" />
        <span className="auth-blob b3" aria-hidden="true" />
        <InnerContainer className="login-inner-container email-verified-inner-container">
          <div className="logo-section">
            <Logo size="medium" showCredit />
          </div>
          <div className="success-icon">&#10003;</div>
          <h2>{t("emailVerification.successTitle")}</h2>
          <p className="message">{t("emailVerification.successMessage")}</p>
          <Button variant="primary" onClick={() => navigate("/login")}>
            {t("emailVerification.goToLogin")}
          </Button>
        </InnerContainer>
      </Container>
    );
  }

  // Error state or resend form
  return (
    <Container className="login-container email-verified-container">
      <span className="auth-blob b1" aria-hidden="true" />
      <span className="auth-blob b2" aria-hidden="true" />
      <span className="auth-blob b3" aria-hidden="true" />
      <div className="banner-text">
        <h1>{t("emailVerification.banner")}</h1>
      </div>
      <InnerContainer className="login-inner-container email-verified-inner-container">
        <div className="logo-section">
          <Logo size="medium" showCredit />
        </div>

        {error && (
          <div className="error-section">
            <div className="error-icon">&#10007;</div>
            <h2>{t("emailVerification.errorTitle")}</h2>
            <p className="message">
              {error === "TokenExpired"
                ? t("emailVerification.tokenExpired")
                : error === "TokenNotFound"
                  ? t("emailVerification.tokenNotFound")
                  : error === "AlreadyVerified"
                    ? t("emailVerification.alreadyVerified")
                    : error === "InvalidToken"
                      ? t("emailVerification.invalidToken")
                      : t("emailVerification.genericError")}
            </p>
          </div>
        )}

        {!resendSuccess ? (
          <>
            <h3>{t("emailVerification.resendTitle")}</h3>
            <Form onSubmit={handleResend} className="resend-form">
              <ErrorText>{resendError}</ErrorText>
              <Form.Group className="mb-3">
                <Form.Control
                  type="email"
                  placeholder={t("emailVerification.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t("emailVerification.sending")
                  : t("emailVerification.resendButton")}
              </Button>
            </Form>
          </>
        ) : (
          <div className="resend-success">
            <div className="success-icon small">&#10003;</div>
            <p>{t("emailVerification.resendSuccess")}</p>
          </div>
        )}

        <div className="login-link">
          <a href="/login">{t("emailVerification.backToLogin")}</a>
        </div>
      </InnerContainer>
    </Container>
  );
};

export { EmailVerified };
